// T22.641 (Gordon 2026-10-22): the ONE plan-downgrade popup.
//
// Every wheel the owner has, with a KEEP checkbox and sortable columns (name,
// size, last edited, views, clones, favorites, unlocks — click a header for
// asc/desc, default = last edited, newest first). A storage meter shows the
// kept bytes (+ Trash) against the new plan's limit and, when the plan caps the
// count (Free), a wheel tally x/limit with created and cloned wheels counted
// together. Save is enabled only when BOTH fit; the server re-checks the same
// math (keepWheelsMath.evaluateKeepSet <-> selection_math.evaluate_keep_set).
// Wheels left unticked become "Archived" (hidden, read-only, never deleted).
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  DEFAULT_SORT,
  SORT_COLUMNS,
  evaluateKeepSet,
  formatBytes,
  nextSort,
  sortWheels,
} from "./keepWheelsMath";
import { BLOCKED_BODY, blockedHeadline } from "./blockedAction";

const PURPLE = "#8F38EF";
const RED = "#C92A2A";

const formatDate = (iso) => {
  const t = Date.parse(iso || "");
  if (!Number.isFinite(t)) return "—";
  return new Date(t).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const initialKeep = (state) => {
  if (Array.isArray(state?.pending_keep_ids) && state.pending_keep_ids.length) {
    return state.pending_keep_ids.map(String);
  }
  return (state?.wheels || []).filter((w) => !w.archived).map((w) => String(w.id));
};

/** Most recently edited first, add each wheel that still fits both limits and
 *  skip one that does not (the same rule the server's deadline auto-pick uses). */
const autoPick = (state) => {
  const rows = sortWheels(state?.wheels || [], "updated_at", "desc");
  const picked = [];
  for (const row of rows) {
    const next = [...picked, String(row.id)];
    const r = evaluateKeepSet(
      state.wheels,
      next,
      state.wheel_limit,
      state.storage_limit,
      state.trash_bytes
    );
    if (!r.fits) continue;
    picked.push(String(row.id));
  }
  return picked;
};

const Meter = ({ label, value, max, ok, testId }) => {
  const pct = max ? Math.min(100, (value / max) * 100) : 0;
  return (
    <Box data-testid={testId} sx={{ flex: "1 1 220px", minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 14,
          mb: 0.5,
          color: "#212529",
        }}
      >
        <span>{label}</span>
        <strong style={{ color: ok ? "#2B8A3E" : RED }}>{ok ? "Fits" : "Over"}</strong>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        aria-label={label}
        sx={{
          height: 10,
          borderRadius: 5,
          backgroundColor: "#E9ECEF",
          "& .MuiLinearProgress-bar": { backgroundColor: ok ? PURPLE : RED },
        }}
      />
    </Box>
  );
};

const KeepWheelsModal = ({
  open,
  state,
  onClose,
  onSave,
  saving = false,
  error = "",
  blockedAction = null,
}) => {
  const isPhone = useMediaQuery("(max-width:600px)");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [keep, setKeep] = useState(() => initialKeep(state));

  useEffect(() => {
    if (open) {
      setKeep(initialKeep(state));
      setSort(DEFAULT_SORT);
    }
  }, [open, state]);

  const rows = useMemo(
    () => sortWheels(state?.wheels || [], sort.key, sort.dir),
    [state, sort]
  );
  const result = useMemo(
    () =>
      evaluateKeepSet(
        state?.wheels || [],
        keep,
        state?.wheel_limit,
        state?.storage_limit,
        state?.trash_bytes
      ),
    [state, keep]
  );

  const hasStorageLimit = state?.storage_limit != null;
  const hasWheelLimit = state?.wheel_limit != null;
  const cooldown = state?.cooldown_until ? formatDate(state.cooldown_until) : null;
  const canSave = result.fits && !cooldown && !saving;
  const keepSet = new Set(keep);

  const toggle = (id) =>
    setKeep((prev) =>
      prev.includes(String(id))
        ? prev.filter((x) => x !== String(id))
        : [...prev, String(id)]
    );

  const reasons = [];
  if (!result.fitsStorage && hasStorageLimit) {
    reasons.push(
      `Free up ${formatBytes(result.usedBytes - state.storage_limit)} more: untick a big wheel or a few small ones.`
    );
  }
  if (!result.fitsWheels && hasWheelLimit) {
    reasons.push(`Keep at most ${state.wheel_limit} wheels.`);
  }
  if (cooldown) reasons.push(`You can change your choice again on ${cooldown}.`);

  const archivedCount = (state?.wheels || []).length - result.keptCount;

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullScreen={isPhone}
      maxWidth="lg"
      fullWidth
      aria-labelledby="keep-wheels-title"
      data-testid="keep-wheels-modal"
    >
      <DialogTitle id="keep-wheels-title" sx={{ pr: 6, fontWeight: 700 }}>
        Choose which wheels to keep
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ px: isPhone ? 1.5 : 3 }}>
        {blockedAction && (
          // T22.747: opened because a create / clone / upload was refused --
          // say so first, or the popup reads as unrelated to what was clicked.
          <Box
            role="alert"
            data-testid="keep-wheels-blocked"
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1.5,
              background: "#FFF4E6",
              border: "1px solid #FFD8A8",
              color: "#5C3D00",
              fontSize: 15,
              lineHeight: 1.5,
            }}
          >
            <strong>{blockedHeadline(blockedAction)}</strong> {BLOCKED_BODY}
          </Box>
        )}
        <Box sx={{ fontSize: 15, color: "#343A40", mb: 2, lineHeight: 1.5 }}>
          Your plan's limits are lower than what you have now. Tick the wheels to
          keep. Wheels you don't keep are <strong>Archived</strong>: hidden from
          everyone but you, read-only, and never deleted. Upgrade any time to get
          them all back.
          {state?.deadline_at && (
            <>
              {" "}
              If you don't choose by <strong>{formatDate(state.deadline_at)}</strong>, we'll
              keep your most recently edited wheels.
            </>
          )}
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          {hasStorageLimit && (
            <Meter
              testId="keep-wheels-storage-meter"
              label={`Storage: ${formatBytes(result.usedBytes)} of ${formatBytes(
                state.storage_limit
              )}${state?.trash_bytes ? ` (incl. ${formatBytes(state.trash_bytes)} in Trash)` : ""}`}
              value={result.usedBytes}
              max={state.storage_limit}
              ok={result.fitsStorage}
            />
          )}
          {hasWheelLimit && (
            <Meter
              testId="keep-wheels-tally"
              label={`Wheels: ${result.keptCount}/${state.wheel_limit} (created + cloned)`}
              value={result.keptCount}
              max={state.wheel_limit}
              ok={result.fitsWheels}
            />
          )}
        </Box>

        {isPhone && (
          // Phone: the table scrolls sideways, so most column headers are
          // off-screen; every sort stays one tap away here.
          <Box
            component="label"
            sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, fontSize: 14, color: "#343A40" }}
          >
            Sort by
            <select
              data-testid="keep-wheels-sort-select"
              value={`${sort.key}:${sort.dir}`}
              onChange={(e) => {
                const [key, dir] = e.target.value.split(":");
                setSort({ key, dir });
              }}
              style={{ flex: 1, minHeight: 36, fontSize: 14, borderRadius: 6, border: "1px solid #CED4DA", padding: "0 8px" }}
            >
              {SORT_COLUMNS.flatMap((col) => [
                <option key={`${col.key}:desc`} value={`${col.key}:desc`}>
                  {col.label} {col.key === "name" ? "(Z–A)" : col.key === "updated_at" ? "(newest)" : "(most)"}
                </option>,
                <option key={`${col.key}:asc`} value={`${col.key}:asc`}>
                  {col.label} {col.key === "name" ? "(A–Z)" : col.key === "updated_at" ? "(oldest)" : "(least)"}
                </option>,
              ])}
            </select>
          </Box>
        )}

        <Box sx={{ overflowX: "auto", border: "1px solid #E9ECEF", borderRadius: 2 }}>
          <Table size="small" stickyHeader aria-label="Your wheels">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ fontWeight: 700 }}>
                  Keep
                </TableCell>
                {SORT_COLUMNS.map((col) => (
                  <TableCell
                    key={col.key}
                    sortDirection={sort.key === col.key ? sort.dir : false}
                    align={col.key === "name" ? "left" : "right"}
                    sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                  >
                    <TableSortLabel
                      active={sort.key === col.key}
                      direction={sort.key === col.key ? sort.dir : "desc"}
                      onClick={() => setSort((s) => nextSort(s, col.key))}
                      data-testid={`keep-wheels-sort-${col.key}`}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((w) => {
                const checked = keepSet.has(String(w.id));
                return (
                  <TableRow
                    key={w.id}
                    hover
                    data-testid="keep-wheels-row"
                    data-wheel-id={w.id}
                    sx={{ opacity: checked ? 1 : 0.72 }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={checked}
                        onChange={() => toggle(w.id)}
                        inputProps={{ "aria-label": `Keep ${w.name}` }}
                        sx={{ "&.Mui-checked": { color: PURPLE } }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: isPhone ? 150 : 220 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        {w.thumbnail ? (
                          <img
                            src={w.thumbnail}
                            alt=""
                            width={36}
                            height={36}
                            style={{ borderRadius: 6, objectFit: "cover", flex: "0 0 36px" }}
                          />
                        ) : (
                          <Box
                            aria-hidden="true"
                            sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "#F1F3F5", flex: "0 0 36px" }}
                          />
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Box
                            sx={{
                              fontWeight: 600,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: isPhone ? 120 : 260,
                            }}
                            title={w.name}
                          >
                            {w.name}
                          </Box>
                          {(w.archived || w.is_cloned) && (
                            <Box sx={{ fontSize: 12, color: "#495057" }}>
                              {[w.archived ? "Archived" : null, w.is_cloned ? "Clone" : null]
                                .filter(Boolean)
                                .join(" · ")}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {formatBytes(w.size)}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {formatDate(w.updated_at)}
                    </TableCell>
                    <TableCell align="right">{w.views}</TableCell>
                    <TableCell align="right">{w.clones}</TableCell>
                    <TableCell align="right">{w.favorites}</TableCell>
                    <TableCell align="right">{w.unlocks}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: isPhone ? 1.5 : 3,
          py: 1.5,
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ fontSize: 13, color: reasons.length || error ? RED : "#495057", flex: "1 1 240px" }} role="status">
          {error ||
            (reasons.length
              ? reasons.join(" ")
              : `Keeping ${result.keptCount}, archiving ${archivedCount}.`)}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={() => setKeep(autoPick(state))}
            sx={{ textTransform: "none", color: PURPLE }}
          >
            Keep most recent
          </Button>
          <Button
            variant="contained"
            disabled={!canSave}
            onClick={() => onSave?.(keep.map(Number))}
            data-testid="keep-wheels-save"
            sx={{
              textTransform: "none",
              bgcolor: PURPLE,
              "&:hover": { bgcolor: "#7A2FD0" },
            }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default KeepWheelsModal;
